import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useRoleTabs } from '../../context/PermissionContext';
import VisitActionsPanel from '../visit/VisitActionsPanel';
import PatientHistory from '../visit/PatientHistory';
import useWorkflowStore, { WORKFLOW_ACTIONS } from '../../stores/workflowStore';
import { formatToken } from '../../utils/formatToken';
import { PhoneButton } from '../common/PhoneButton';
import {
  patientName, StatPill,
  orderTabsByPatterns,
  EmptyState,
} from './WorkflowShared';

// AdminView — Per-doctor queue summary with expandable visit details
export default function AdminView({ inProgress, queue, dailyVisits, doctors, isDark, onNavigate, onOpenRegister }) {
  const [expandedDoctor, setExpandedDoctor] = useState(null); // doctor id
  const [expandedVisit, setExpandedVisit] = useState(null);   // visit_id
  const [activeTab, setActiveTab] = useState(null);
  const [historyPatient, setHistoryPatient] = useState(null);

  const { logAction: logWorkflow, patterns, fetchPatterns } = useWorkflowStore();

  useEffect(() => {
    if (!patterns) fetchPatterns();
  }, [patterns, fetchPatterns]);

  // Group by doctor
  const doctorGroups = useMemo(() => {
    const map = {};
    [...inProgress, ...queue].forEach((v) => {
      const id = v.doctor_member_id || v.visit_doctor_id || 'unknown';
      if (!map[id]) {
        map[id] = { id, name: v.doctor_name || 'Unknown', queue: 0, active: 0, visits: [] };
      }
      map[id].visits.push(v);
      if (v.status === 'QUEUE') map[id].queue++;
      else map[id].active++;
    });
    return Object.values(map);
  }, [inProgress, queue]);

  const totalActive = inProgress.length + queue.length;
  const totalDone = (dailyVisits || []).filter(
    (v) => v.status === 'COMPLETED'
  ).length;

  // Admin tabs — derived from central permissions (full access)
  const ADMIN_TABS = useRoleTabs();
  const orderedAdminTabs = useMemo(() => orderTabsByPatterns(ADMIN_TABS, patterns), [ADMIN_TABS, patterns]);

  const handleToggleDoctor = useCallback((docId) => {
    setExpandedDoctor((prev) => {
      if (prev === docId) return null;
      setExpandedVisit(null);
      setActiveTab(null);
      return docId;
    });
  }, []);

  const handleToggleVisit = useCallback((visitId) => {
    setExpandedVisit((prev) => {
      if (prev === visitId) { setActiveTab(null); return null; }
      setActiveTab(null);
      return visitId;
    });
  }, []);

  const handleTabToggle = useCallback((key) => {
    setActiveTab((prev) => (prev === key ? null : key));
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Overview row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <StatPill label="Active" value={totalActive} color="#8b5cf6" isDark={isDark} />
        <StatPill label="Queue" value={queue.length} color="#3b82f6" isDark={isDark} />
        <StatPill label="In Progress" value={inProgress.length} color="#a78bfa" isDark={isDark} />
        <StatPill label="Completed" value={totalDone} color="#4ade80" isDark={isDark} />
      </div>

      {/* Doctor-wise breakdown — expandable */}
      {doctorGroups.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {doctorGroups.map((doc) => {
            const isDocExpanded = expandedDoctor === doc.id;
            return (
              <div
                key={doc.id}
                style={{
                  borderRadius: '0.625rem',
                  border: `1px solid ${isDocExpanded
                    ? isDark ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.15)'
                    : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                  overflow: 'hidden',
                  transition: 'border-color 0.15s',
                }}
              >
                {/* Doctor header */}
                <button
                  onClick={() => handleToggleDoctor(doc.id)}
                  style={{
                    width: '100%', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.625rem 0.75rem',
                    background: 'transparent', border: 'none', textAlign: 'left',
                  }}
                >
                  <div>
                    {(() => {
                      const docProfile = (doctors || []).find((d) => (d.member_id || d.doctor_id) === doc.id);
                      const avail = docProfile?.availability || 'available';
                      const availBadge = { available: { icon: '🟢', label: 'Available', color: '#22c55e' }, on_hold: { icon: '🟡', label: 'On Hold', color: '#f59e0b' }, closed_today: { icon: '🔴', label: 'Closed', color: '#ef4444' } }[avail];
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <p style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
                            🩺 Dr. {doc.name}
                          </p>
                          {avail !== 'available' && (
                            <span style={{ fontSize: '0.5625rem', whiteSpace: 'nowrap', color: availBadge?.color }}>
                              {availBadge?.icon} {availBadge?.label}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <p style={{ margin: '0.125rem 0 0', fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                      {doc.queue} in queue · {doc.active} active
                    </p>
                  </div>
                  <span style={{ fontSize: '0.625rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                    {isDocExpanded ? '▲' : '▼'}
                  </span>
                </button>

                {/* Patient list within doctor */}
                {isDocExpanded && (
                  <div style={{ padding: '0 0.5rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {doc.visits.map((visit) => {
                      const isVisitExpanded = expandedVisit === visit.visit_id;
                      return (
                        <div
                          key={visit.visit_id}
                          style={{
                            borderRadius: '0.5rem',
                            border: `1px solid ${isVisitExpanded
                              ? isDark ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.15)'
                              : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
                            background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.8)',
                            overflow: 'hidden',
                          }}
                        >
                          <button
                            onClick={() => handleToggleVisit(visit.visit_id)}
                            style={{
                              width: '100%', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '0.5rem',
                              padding: '0.5rem 0.625rem',
                              background: 'transparent', border: 'none', textAlign: 'left',
                            }}
                          >
                            <div
                              style={{
                                width: '0.4375rem', height: '0.4375rem', borderRadius: '50%',
                                background: visit.status === 'IN_PROGRESS' ? '#8b5cf6' : '#3b82f6',
                                flexShrink: 0,
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                <p style={{
                                  margin: 0, fontSize: '0.75rem', fontWeight: 600,
                                  color: isDark ? '#f5f5f7' : '#111827',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  {patientName(visit)}
                                  {visit.queue_number ? (
                                    <span style={{ fontWeight: 400, color: isDark ? '#6b7280' : '#9ca3af', marginLeft: '0.25rem', fontSize: '0.625rem' }}>
                                      {formatToken(visit.queue_number, visit.appointment_number)}
                                    </span>
                                  ) : null}
                                </p>
                                <PhoneButton phoneNumber={visit.patient_mobile} isDark={isDark} size="sm" />
                              </div>
                            </div>
                            <span style={{
                              fontSize: '0.5625rem', fontWeight: 500,
                              color: visit.status === 'IN_PROGRESS' ? '#8b5cf6' : '#3b82f6',
                              background: visit.status === 'IN_PROGRESS'
                                ? isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.08)'
                                : isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.06)',
                              padding: '0.0625rem 0.375rem',
                              borderRadius: '0.25rem',
                              flexShrink: 0,
                            }}>
                              {visit.status === 'IN_PROGRESS' ? 'Active' : 'Queue'}
                            </span>
                            <span style={{ fontSize: '0.5rem', color: isDark ? '#6b7280' : '#9ca3af', flexShrink: 0 }}>
                              {isVisitExpanded ? '▲' : '▼'}
                            </span>
                          </button>

                          {/* Inline actions for expanded visit */}
                          {isVisitExpanded && (
                            <div style={{ padding: '0 0.625rem 0.5rem', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                              <div style={{ display: 'flex', gap: '0.3125rem', flexWrap: 'wrap', padding: '0.375rem 0 0.25rem' }}>
                                {orderedAdminTabs.map((tab) => {
                                  const isActive = activeTab === tab.key;
                                  const isHistory = tab.key === 'history';
                                  return (
                                    <button
                                      key={tab.key}
                                      onClick={() => {
                                        if (isHistory) {
                                          logWorkflow(visit.visit_id, WORKFLOW_ACTIONS.VIEW_PATIENT_HISTORY);
                                          setHistoryPatient({ patient_id: visit.patient_id, patient_name: patientName(visit), mobile: visit.patient_mobile });
                                        } else {
                                          handleTabToggle(tab.key);
                                        }
                                      }}
                                      style={{
                                        cursor: 'pointer',
                                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                        padding: '0.3125rem 0.5rem', borderRadius: '0.375rem',
                                        fontSize: '0.625rem', fontWeight: isActive ? 600 : 400,
                                        border: isActive
                                          ? `1.5px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                                          : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                                        background: isActive
                                          ? isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)'
                                          : 'transparent',
                                        color: isActive ? '#3b82f6' : isDark ? '#d4d4d8' : '#374151',
                                        transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                                      }}
                                    >
                                      <span>{tab.icon}</span>
                                      <span>{tab.label}</span>
                                      {isActive && !isHistory && <span style={{ fontSize: '0.4375rem', marginLeft: '0.0625rem', opacity: 0.7 }}>▲</span>}
                                    </button>
                                  );
                                })}
                              </div>

                              {activeTab && activeTab !== 'history' && (
                                <VisitActionsPanel
                                  visit={visit}
                                  userRole="ADMIN"
                                  initialTab={activeTab}
                                  hideActionBar
                                  onClose={() => setActiveTab(null)}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState isDark={isDark} icon="✅" title="All done" subtitle={`All consultations complete · ${totalDone} visits today`} />
      )}

      {/* Register CTA when queue is low */}
      {queue.length < 3 && onOpenRegister && (
        <button
          onClick={onOpenRegister}
          style={{
            cursor: 'pointer',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            border: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            background: 'transparent',
            fontSize: '0.75rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            textAlign: 'center',
            transition: 'background 0.15s, opacity 0.15s',
          }}
        >
          + Register New Visit
        </button>
      )}

      {/* Patient History Modal (portaled) */}
      {historyPatient && (
        <PatientHistory
          patientId={historyPatient.patient_id}
          patientName={historyPatient.patient_name}
          phoneNumber={historyPatient.mobile}
          onClose={() => setHistoryPatient(null)}
        />
      )}
    </div>
  );
}


